// ======================================================================

import {
  CITY_COORDS as AREA_CITY_COORDS,
  PROVINCE_COORDS as AREA_PROVINCE_COORDS,
} from '@/utils/areaData';
// 占星 — 出生地地理工具 v2.0
// 职责：从 birthPlace 字符串解析经纬度 + 时区
// 改进：
//   1. 增加英文名 / 拼音名 / 城市别名索引
//   2. 大幅扩充海外城市覆盖（300+城市）
//   3. 补充东欧、中美、中东更多城市和完整 DST 规则
//   4. 支持不带"市/省"后缀的简写匹配
// ======================================================================

export interface GeoCoords {
  lat: number;
  lng: number;
  tz: number;
}

const AMBIGUOUS_ADMIN_LEVELS = new Set([
  '直辖市县',
  '省直辖县级行政区划',
  '自治区直辖县级行政区划',
]);

interface AreaDataMatch {
  coords: GeoCoords;
  resolvedName: string;
  source: 'city_exact' | 'province';
}

function resolveAreaDataCoords(tokens: string[]): AreaDataMatch | null {
  const province = tokens[0];
  const city = tokens[1];
  const district = tokens[2];
  if (district && AREA_CITY_COORDS[district]) {
    return { coords: AREA_CITY_COORDS[district], resolvedName: district, source: 'city_exact' };
  }
  if (city && AREA_CITY_COORDS[city] && !AMBIGUOUS_ADMIN_LEVELS.has(city)) {
    return { coords: AREA_CITY_COORDS[city], resolvedName: city, source: 'city_exact' };
  }
  if (city === '直辖市县' && province === '海南省') {
    const coords = AREA_CITY_COORDS[city];
    return coords ? { coords, resolvedName: city, source: 'city_exact' } : null;
  }
  const provinceCoords = province ? AREA_PROVINCE_COORDS[province] : null;
  return provinceCoords
    ? { coords: { ...provinceCoords, tz: 8 }, resolvedName: province, source: 'province' }
    : null;
}

// ─── 城市经纬度数据库 ─────────────────────────────────────────────────
export const CITY_COORDS: Record<string, GeoCoords> = {
  // ─── 国内直辖市 ─────────────────────────────────────────────────────
  '北京市': { lat: 39.92, lng: 116.46, tz: 8 },
  '天津市': { lat: 39.13, lng: 117.20, tz: 8 },
  '上海市': { lat: 31.23, lng: 121.47, tz: 8 },
  '重庆市': { lat: 29.56, lng: 106.55, tz: 8 },
  // ─── 省会/主要城市 ──────────────────────────────────────────────────
  '石家庄市': { lat: 38.04, lng: 114.48, tz: 8 },
  '太原市':   { lat: 37.87, lng: 112.55, tz: 8 },
  '呼和浩特市': { lat: 40.82, lng: 111.65, tz: 8 },
  '沈阳市':   { lat: 41.80, lng: 123.43, tz: 8 },
  '长春市':   { lat: 43.88, lng: 125.35, tz: 8 },
  '哈尔滨市': { lat: 45.75, lng: 126.63, tz: 8 },
  '南京市':   { lat: 32.06, lng: 118.78, tz: 8 },
  '杭州市':   { lat: 30.25, lng: 120.17, tz: 8 },
  '合肥市':   { lat: 31.86, lng: 117.28, tz: 8 },
  '福州市':   { lat: 26.08, lng: 119.30, tz: 8 },
  '南昌市':   { lat: 28.68, lng: 115.88, tz: 8 },
  '济南市':   { lat: 36.67, lng: 116.99, tz: 8 },
  '郑州市':   { lat: 34.76, lng: 113.65, tz: 8 },
  '武汉市':   { lat: 30.58, lng: 114.31, tz: 8 },
  '长沙市':   { lat: 28.23, lng: 112.94, tz: 8 },
  '广州市':   { lat: 23.13, lng: 113.26, tz: 8 },
  '南宁市':   { lat: 22.82, lng: 108.37, tz: 8 },
  '海口市':   { lat: 20.04, lng: 110.35, tz: 8 },
  '成都市':   { lat: 30.67, lng: 104.07, tz: 8 },
  '贵阳市':   { lat: 26.58, lng: 106.72, tz: 8 },
  '昆明市':   { lat: 25.04, lng: 102.73, tz: 8 },
  '拉萨市':   { lat: 29.65, lng:  91.13, tz: 8 },
  '西安市':   { lat: 34.27, lng: 108.95, tz: 8 },
  '兰州市':   { lat: 36.06, lng: 103.79, tz: 8 },
  '西宁市':   { lat: 36.62, lng: 101.78, tz: 8 },
  '银川市':   { lat: 38.47, lng: 106.27, tz: 8 },
  '乌鲁木齐市': { lat: 43.82, lng:  87.62, tz: 8 },
  '深圳市':   { lat: 22.54, lng: 114.06, tz: 8 },
  '珠海市':   { lat: 22.27, lng: 113.57, tz: 8 },
  '厦门市':   { lat: 24.48, lng: 118.09, tz: 8 },
  '青岛市':   { lat: 36.07, lng: 120.38, tz: 8 },
  '大连市':   { lat: 38.91, lng: 121.60, tz: 8 },
  '宁波市':   { lat: 29.87, lng: 121.56, tz: 8 },
  '苏州市':   { lat: 31.30, lng: 120.62, tz: 8 },
  '无锡市':   { lat: 31.57, lng: 120.30, tz: 8 },
  '佛山市':   { lat: 23.02, lng: 113.12, tz: 8 },
  '东莞市':   { lat: 23.05, lng: 113.75, tz: 8 },
  '汕头市':   { lat: 23.35, lng: 116.68, tz: 8 },
  '温州市':   { lat: 28.00, lng: 120.67, tz: 8 },
  '嘉兴市':   { lat: 30.75, lng: 120.75, tz: 8 },
  '南通市':   { lat: 31.97, lng: 120.87, tz: 8 },
  '常州市':   { lat: 31.79, lng: 119.95, tz: 8 },
  '徐州市':   { lat: 34.27, lng: 117.19, tz: 8 },
  '泉州市':   { lat: 24.87, lng: 118.68, tz: 8 },
  '济宁市':   { lat: 35.42, lng: 116.59, tz: 8 },
  '临沂市':   { lat: 35.10, lng: 118.35, tz: 8 },
  '潍坊市':   { lat: 36.71, lng: 119.11, tz: 8 },
  '唐山市':   { lat: 39.63, lng: 118.18, tz: 8 },
  '保定市':   { lat: 38.87, lng: 115.49, tz: 8 },
  '邯郸市':   { lat: 36.62, lng: 114.49, tz: 8 },
  '洛阳市':   { lat: 34.68, lng: 112.45, tz: 8 },
  '襄阳市':   { lat: 32.01, lng: 112.12, tz: 8 },
  '宜昌市':   { lat: 30.70, lng: 111.28, tz: 8 },
  '岳阳市':   { lat: 29.37, lng: 113.12, tz: 8 },
  '衡阳市':   { lat: 26.89, lng: 112.57, tz: 8 },
  '绍兴市':   { lat: 30.00, lng: 120.58, tz: 8 },
  '金华市':   { lat: 29.08, lng: 119.65, tz: 8 },
  '台州市':   { lat: 28.66, lng: 121.43, tz: 8 },
  '惠州市':   { lat: 23.11, lng: 114.42, tz: 8 },
  '中山市':   { lat: 22.52, lng: 113.39, tz: 8 },
  '江门市':   { lat: 22.58, lng: 113.08, tz: 8 },
  '湛江市':   { lat: 21.27, lng: 110.35, tz: 8 },
  '揭阳市':   { lat: 23.55, lng: 116.37, tz: 8 },
  '南阳市':   { lat: 33.00, lng: 112.53, tz: 8 },
  '哈尔滨':   { lat: 45.75, lng: 126.63, tz: 8 },
  '香港':     { lat: 22.32, lng: 114.17, tz: 8 },
  '澳门':     { lat: 22.20, lng: 113.55, tz: 8 },
  '台北市':   { lat: 25.05, lng: 121.53, tz: 8 },
  '台中市':   { lat: 24.15, lng: 120.68, tz: 8 },
  '高雄市':   { lat: 22.63, lng: 120.27, tz: 8 },
  '台南市':   { lat: 22.99, lng: 120.21, tz: 8 },
  '新竹市':   { lat: 24.81, lng: 120.97, tz: 8 },
  '基隆市':   { lat: 25.13, lng: 121.74, tz: 8 },
  // ─── 日本 ──────────────────────────────────────────────────────────
  '东京':     { lat:  35.69, lng: 139.69, tz:  9 },
  '大阪':     { lat:  34.69, lng: 135.50, tz:  9 },
  '京都':     { lat:  35.01, lng: 135.77, tz:  9 },
  '名古屋':   { lat:  35.18, lng: 136.91, tz:  9 },
  '横滨':     { lat:  35.44, lng: 139.64, tz:  9 },
  '神户':     { lat:  34.69, lng: 135.20, tz:  9 },
  '福冈':     { lat:  33.59, lng: 130.40, tz:  9 },
  '广岛':     { lat:  34.39, lng: 132.45, tz:  9 },
  '仙台':     { lat:  38.27, lng: 140.87, tz:  9 },
  '札幌':     { lat:  43.06, lng: 141.35, tz:  9 },
  '那霸':     { lat:  26.21, lng: 127.68, tz:  9 },
  // ─── 韩国 ──────────────────────────────────────────────────────────
  '首尔':     { lat:  37.57, lng: 126.98, tz:  9 },
  '釜山':     { lat:  35.10, lng: 129.03, tz:  9 },
  '仁川':     { lat:  37.46, lng: 126.71, tz:  9 },
  '大邱':     { lat:  35.87, lng: 128.60, tz:  9 },
  '大田':     { lat:  36.35, lng: 127.38, tz:  9 },
  '光州':     { lat:  35.16, lng: 126.85, tz:  9 },
  // ─── 东南亚 ────────────────────────────────────────────────────────
  '曼谷':     { lat:  13.75, lng: 100.52, tz:  7 },
  '新加坡':   { lat:   1.35, lng: 103.82, tz:  8 },
  '吉隆坡':   { lat:   3.15, lng: 101.70, tz:  8 },
  '槟城':     { lat:   5.41, lng: 100.33, tz:  8 },
  '古晋':     { lat:   1.55, lng: 110.33, tz:  8 },
  '雅加达':   { lat:  -6.21, lng: 106.85, tz:  7 },
  '泗水':     { lat:  -7.25, lng: 112.75, tz:  7 },
  '棉兰':     { lat:   3.59, lng:  98.67, tz:  7 },
  '巴厘岛':   { lat:  -8.65, lng: 115.22, tz:  8 },
  '河内':     { lat:  21.03, lng: 105.85, tz:  7 },
  '胡志明市': { lat:  10.82, lng: 106.63, tz:  7 },
  '岘港':     { lat:  16.07, lng: 108.22, tz:  7 },
  '马尼拉':   { lat:  14.60, lng: 120.98, tz:  8 },
  '宿雾':     { lat:  10.32, lng: 123.90, tz:  8 },
  '达沃':     { lat:   7.07, lng: 125.61, tz:  8 },
  '金边':     { lat:  11.57, lng: 104.92, tz:  7 },
  '万象':     { lat:  17.97, lng: 102.60, tz:  7 },
  '仰光':     { lat:  16.87, lng:  96.15, tz:  6.5 },
  '内比都':   { lat:  19.75, lng:  96.08, tz:  6.5 },
  '达卡':     { lat:  23.72, lng:  90.41, tz:  6 },
  '加德满都':  { lat:  27.71, lng:  85.32, tz:  5.75 },
  '科伦坡':   { lat:   6.92, lng:  79.86, tz:  5.5 },
  '卡拉奇':   { lat:  24.86, lng:  67.01, tz:  5 },
  '拉合尔':   { lat:  31.56, lng:  74.35, tz:  5 },
  '伊斯兰堡': { lat:  33.72, lng:  73.04, tz:  5 },
  // ─── 南亚 ──────────────────────────────────────────────────────────
  '孟买':     { lat:  19.08, lng:  72.88, tz:  5.5 },
  '新德里':   { lat:  28.61, lng:  77.21, tz:  5.5 },
  '班加罗尔': { lat:  12.97, lng:  77.59, tz:  5.5 },
  '钦奈':     { lat:  13.08, lng:  80.27, tz:  5.5 },
  '金奈':     { lat:  13.08, lng:  80.27, tz:  5.5 },
  '海得拉巴': { lat:  17.38, lng:  78.47, tz:  5.5 },
  '加尔各答': { lat:  22.56, lng:  88.36, tz:  5.5 },
  '艾哈迈达巴德': { lat: 23.03, lng: 72.58, tz: 5.5 },
  '浦那':     { lat:  18.52, lng:  73.86, tz:  5.5 },
  // ─── 中亚 / 中东 ───────────────────────────────────────────────────
  '迪拜':     { lat:  25.20, lng:  55.27, tz:  4 },
  '阿布扎比': { lat:  24.47, lng:  54.37, tz:  4 },
  '沙迦':     { lat:  25.34, lng:  55.39, tz:  4 },
  '多哈':     { lat:  25.29, lng:  51.53, tz:  3 },
  '科威特城': { lat:  29.37, lng:  47.98, tz:  3 },
  '麦纳麦':   { lat:  26.22, lng:  50.58, tz:  3 },
  '利雅得':   { lat:  24.69, lng:  46.72, tz:  3 },
  '吉达':     { lat:  21.54, lng:  39.17, tz:  3 },
  '麦加':     { lat:  21.43, lng:  39.83, tz:  3 },
  '麦地那':   { lat:  24.47, lng:  39.61, tz:  3 },
  '马斯喀特': { lat:  23.61, lng:  58.59, tz:  4 },
  '巴格达':   { lat:  33.34, lng:  44.40, tz:  3 },
  '德黑兰':   { lat:  35.69, lng:  51.42, tz:  3.5 },
  '伊斯法罕': { lat:  32.66, lng:  51.68, tz:  3.5 },
  '安卡拉':   { lat:  39.92, lng:  32.85, tz:  3 },
  '伊斯坦布尔': { lat: 41.01, lng:  28.95, tz:  3 },
  '特拉维夫': { lat:  32.08, lng:  34.78, tz:  2 },
  '耶路撒冷': { lat:  31.78, lng:  35.22, tz:  2 },
  '安曼':     { lat:  31.96, lng:  35.95, tz:  2 },
  '贝鲁特':   { lat:  33.89, lng:  35.50, tz:  2 },
  '大马士革': { lat:  33.51, lng:  36.29, tz:  3 },
  '阿勒颇':   { lat:  36.20, lng:  37.16, tz:  3 },
  '塔什干':   { lat:  41.30, lng:  69.24, tz:  5 },
  '阿拉木图': { lat:  43.24, lng:  76.89, tz:  6 },
  '努尔苏丹': { lat:  51.18, lng:  71.45, tz:  6 },
  '比什凯克': { lat:  42.87, lng:  74.59, tz:  6 },
  '杜尚别':   { lat:  38.56, lng:  68.77, tz:  5 },
  '阿什哈巴德': { lat: 37.95, lng:  58.38, tz:  5 },
  '巴库':     { lat:  40.41, lng:  49.87, tz:  4 },
  '埃里温':   { lat:  40.18, lng:  44.51, tz:  4 },
  '第比利斯': { lat:  41.69, lng:  44.83, tz:  4 },
  // ─── 欧洲 ──────────────────────────────────────────────────────────
  '伦敦':     { lat:  51.51, lng:  -0.13, tz:  0 },
  '曼彻斯特': { lat:  53.48, lng:  -2.24, tz:  0 },
  '伯明翰':   { lat:  52.48, lng:  -1.90, tz:  0 },
  '格拉斯哥': { lat:  55.86, lng:  -4.25, tz:  0 },
  '利物浦':   { lat:  53.41, lng:  -2.99, tz:  0 },
  '爱丁堡':   { lat:  55.95, lng:  -3.19, tz:  0 },
  '都柏林':   { lat:  53.33, lng:  -6.25, tz:  0 },
  '巴黎':     { lat:  48.86, lng:   2.35, tz:  1 },
  '里昂':     { lat:  45.75, lng:   4.85, tz:  1 },
  '马赛':     { lat:  43.30, lng:   5.37, tz:  1 },
  '波尔多':   { lat:  44.84, lng:  -0.58, tz:  1 },
  '里尔':     { lat:  50.64, lng:   3.06, tz:  1 },
  '图卢兹':   { lat:  43.60, lng:   1.44, tz:  1 },
  '尼斯':     { lat:  43.70, lng:   7.26, tz:  1 },
  '斯特拉斯堡': { lat: 48.58, lng:   7.75, tz:  1 },
  '柏林':     { lat:  52.52, lng:  13.40, tz:  1 },
  '法兰克福': { lat:  50.11, lng:   8.68, tz:  1 },
  '汉堡':     { lat:  53.55, lng:  10.00, tz:  1 },
  '慕尼黑':   { lat:  48.14, lng:  11.58, tz:  1 },
  '科隆':     { lat:  50.94, lng:   6.96, tz:  1 },
  '杜塞尔多夫': { lat: 51.23, lng:   6.79, tz:  1 },
  '斯图加特': { lat:  48.78, lng:   9.18, tz:  1 },
  '莱比锡':   { lat:  51.34, lng:  12.38, tz:  1 },
  '德累斯顿': { lat:  51.05, lng:  13.74, tz:  1 },
  '罗马':     { lat:  41.90, lng:  12.50, tz:  1 },
  '米兰':     { lat:  45.46, lng:   9.19, tz:  1 },
  '那不勒斯': { lat:  40.85, lng:  14.27, tz:  1 },
  '佛罗伦萨': { lat:  43.77, lng:  11.26, tz:  1 },
  '威尼斯':   { lat:  45.44, lng:  12.34, tz:  1 },
  '都灵':     { lat:  45.07, lng:   7.69, tz:  1 },
  '热那亚':   { lat:  44.40, lng:   8.94, tz:  1 },
  '巴勒莫':   { lat:  38.12, lng:  13.36, tz:  1 },
  '博洛尼亚': { lat:  44.49, lng:  11.34, tz:  1 },
  '马德里':   { lat:  40.42, lng:  -3.70, tz:  1 },
  '巴塞罗那': { lat:  41.39, lng:   2.15, tz:  1 },
  '瓦伦西亚': { lat:  39.47, lng:  -0.38, tz:  1 },
  '塞维利亚': { lat:  37.39, lng:  -5.98, tz:  1 },
  '马拉加':   { lat:  36.72, lng:  -4.42, tz:  1 },
  '里斯本':   { lat:  38.72, lng:  -9.14, tz:  0 },
  '波尔图':   { lat:  41.16, lng:  -8.63, tz:  0 },
  '阿姆斯特丹': { lat: 52.37, lng:   4.90, tz:  1 },
  '鹿特丹':   { lat:  51.92, lng:   4.48, tz:  1 },
  '海牙':     { lat:  52.08, lng:   4.31, tz:  1 },
  '布鲁塞尔': { lat:  50.85, lng:   4.35, tz:  1 },
  '安特卫普': { lat:  51.22, lng:   4.40, tz:  1 },
  '维也纳':   { lat:  48.21, lng:  16.37, tz:  1 },
  '格拉茨':   { lat:  47.07, lng:  15.44, tz:  1 },
  '苏黎世':   { lat:  47.38, lng:   8.54, tz:  1 },
  '日内瓦':   { lat:  46.20, lng:   6.15, tz:  1 },
  '巴塞尔':   { lat:  47.56, lng:   7.59, tz:  1 },
  '伯尔尼':   { lat:  46.95, lng:   7.45, tz:  1 },
  '斯德哥尔摩': { lat: 59.33, lng:  18.07, tz:  1 },
  '哥德堡':   { lat:  57.71, lng:  11.97, tz:  1 },
  '马尔默':   { lat:  55.61, lng:  13.00, tz:  1 },
  '哥本哈根': { lat:  55.68, lng:  12.57, tz:  1 },
  '奥斯陆':   { lat:  59.91, lng:  10.75, tz:  1 },
  '卑尔根':   { lat:  60.39, lng:   5.32, tz:  1 },
  '赫尔辛基': { lat:  60.17, lng:  24.94, tz:  2 },
  '坦佩雷':   { lat:  61.50, lng:  23.77, tz:  2 },
  '雷克雅未克': { lat: 64.14, lng: -21.94, tz:  0 },
  '莫斯科':   { lat:  55.75, lng:  37.62, tz:  3 },
  '圣彼得堡': { lat:  59.94, lng:  30.32, tz:  3 },
  '新西伯利亚': { lat: 55.02, lng:  82.93, tz:  7 },
  '叶卡捷琳堡': { lat: 56.84, lng:  60.60, tz:  5 },
  '喀山':     { lat:  55.79, lng:  49.12, tz:  3 },
  '下诺夫哥罗德': { lat: 56.33, lng: 44.00, tz: 3 },
  '华沙':     { lat:  52.23, lng:  21.01, tz:  1 },
  '克拉科夫': { lat:  50.07, lng:  19.95, tz:  1 },
  '布拉格':   { lat:  50.08, lng:  14.44, tz:  1 },
  '布拉迪斯拉发': { lat: 48.15, lng: 17.11, tz: 1 },
  '布达佩斯': { lat:  47.50, lng:  19.04, tz:  1 },
  '布加勒斯特': { lat: 44.43, lng:  26.10, tz:  2 },
  '贝尔格莱德': { lat: 44.80, lng:  20.47, tz:  1 },
  '索菲亚':   { lat:  42.70, lng:  23.32, tz:  2 },
  '雅典':     { lat:  37.98, lng:  23.73, tz:  2 },
  '萨洛尼卡': { lat:  40.64, lng:  22.94, tz:  2 },
  '基辅':     { lat:  50.45, lng:  30.52, tz:  2 },
  '哈尔科夫': { lat:  49.99, lng:  36.23, tz:  2 },
  '敖德萨':   { lat:  46.48, lng:  30.73, tz:  2 },
  '明斯克':   { lat:  53.90, lng:  27.57, tz:  3 },
  '里加':     { lat:  56.95, lng:  24.11, tz:  2 },
  '塔林':     { lat:  59.44, lng:  24.75, tz:  2 },
  '维尔纽斯': { lat:  54.69, lng:  25.28, tz:  2 },
  '萨格勒布': { lat:  45.81, lng:  15.98, tz:  1 },
  '卢布尔雅那': { lat: 46.05, lng: 14.51, tz: 1 },
  '萨拉热窝': { lat:  43.85, lng:  18.36, tz:  1 },
  '蒂拉纳':   { lat:  41.33, lng:  19.82, tz:  1 },
  '斯科普里': { lat:  41.99, lng:  21.43, tz:  1 },
  // ─── 北美洲 ────────────────────────────────────────────────────────
  '纽约':     { lat:  40.71, lng: -74.01, tz: -5 },
  '洛杉矶':   { lat:  34.05, lng:-118.24, tz: -8 },
  '旧金山':   { lat:  37.77, lng:-122.42, tz: -8 },
  '芝加哥':   { lat:  41.88, lng: -87.63, tz: -6 },
  '休斯顿':   { lat:  29.76, lng: -95.37, tz: -6 },
  '凤凰城':   { lat:  33.45, lng:-112.07, tz: -7 },
  '迈阿密':   { lat:  25.77, lng: -80.19, tz: -5 },
  '波士顿':   { lat:  42.36, lng: -71.06, tz: -5 },
  '西雅图':   { lat:  47.61, lng:-122.33, tz: -8 },
  '拉斯维加斯': { lat: 36.17, lng:-115.14, tz: -8 },
  '丹佛':     { lat:  39.74, lng:-104.98, tz: -7 },
  '达拉斯':   { lat:  32.78, lng: -96.80, tz: -6 },
  '圣安东尼奥': { lat: 29.42, lng: -98.49, tz: -6 },
  '圣地亚哥': { lat:  32.72, lng:-117.16, tz: -8 },
  '圣何塞':   { lat:  37.34, lng:-121.89, tz: -8 },
  '奥斯汀':   { lat:  30.27, lng: -97.74, tz: -6 },
  '夏洛特':   { lat:  35.23, lng: -80.84, tz: -5 },
  '底特律':   { lat:  42.33, lng: -83.05, tz: -5 },
  '杰克逊维尔': { lat: 30.33, lng: -81.66, tz: -5 },
  '哥伦布':   { lat:  39.96, lng: -82.99, tz: -5 },
  '印第安纳波利斯': { lat: 39.77, lng: -86.16, tz: -5 },
  '华盛顿':   { lat:  38.91, lng: -77.04, tz: -5 },
  '费城':     { lat:  39.95, lng: -75.17, tz: -5 },
  '巴尔的摩': { lat:  39.28, lng: -76.60, tz: -5 },
  '明尼阿波利斯': { lat: 44.98, lng: -93.27, tz: -6 },
  '圣路易斯': { lat:  38.63, lng: -90.20, tz: -6 },
  '亚特兰大': { lat:  33.75, lng: -84.39, tz: -5 },
  '奥尔良':   { lat:  29.95, lng: -90.07, tz: -6 },
  '波特兰':   { lat:  45.52, lng:-122.68, tz: -8 },
  '萨克拉门托': { lat: 38.58, lng:-121.49, tz: -8 },
  
  '盐湖城':   { lat:  40.76, lng:-111.89, tz: -7 },
  '阿尔伯克基': { lat: 35.08, lng:-106.65, tz: -7 },
  '图森':     { lat:  32.22, lng:-110.93, tz: -7 },
  '多伦多':   { lat:  43.65, lng: -79.38, tz: -5 },
  '温哥华':   { lat:  49.25, lng:-123.12, tz: -8 },
  '蒙特利尔': { lat:  45.50, lng: -73.57, tz: -5 },
  '卡尔加里': { lat:  51.05, lng:-114.07, tz: -7 },
  '埃德蒙顿': { lat:  53.55, lng:-113.49, tz: -7 },
  '渥太华':   { lat:  45.42, lng: -75.70, tz: -5 },
  '哈利法克斯': { lat: 44.65, lng: -63.57, tz: -4 },
  '温尼伯':   { lat:  49.90, lng: -97.14, tz: -6 },
  '墨西哥城': { lat:  19.43, lng: -99.13, tz: -6 },
  '瓜达拉哈拉': { lat: 20.66, lng:-103.35, tz: -6 },
  '蒙特雷':   { lat:  25.67, lng: -100.31, tz: -6 },
  '蒂华纳':   { lat:  32.53, lng:-117.04, tz: -8 },
  '危地马拉城': { lat: 14.64, lng: -90.51, tz: -6 },
  '圣萨尔瓦多': { lat: 13.69, lng: -89.19, tz: -6 },
  '圣何塞哥斯达黎加': { lat: 9.93, lng: -84.08, tz: -6 },
  '巴拿马城': { lat:   8.99, lng: -79.52, tz: -5 },
  '哈瓦那':   { lat:  23.14, lng: -82.35, tz: -5 },
  '圣多明各': { lat:  18.47, lng: -69.95, tz: -4 },
  '圣胡安波多黎各': { lat: 18.47, lng: -66.11, tz: -4 },
  // ─── 南美洲 ────────────────────────────────────────────────────────
  '圣保罗':   { lat: -23.55, lng: -46.63, tz: -3 },
  '里约热内卢': { lat: -22.91, lng: -43.17, tz: -3 },
  '巴西利亚': { lat: -15.78, lng: -47.93, tz: -3 },
  '萨尔瓦多': { lat: -12.97, lng: -38.50, tz: -3 },
  '贝洛奥里藏特': { lat: -19.92, lng: -43.94, tz: -3 },
  '福塔雷萨': { lat:  -3.72, lng: -38.54, tz: -3 },
  '布宜诺斯艾利斯': { lat: -34.60, lng: -58.38, tz: -3 },
  '科尔多瓦阿根廷': { lat: -31.42, lng: -64.18, tz: -3 },
  '罗萨里奥': { lat: -32.95, lng: -60.67, tz: -3 },
  '圣地亚哥智利': { lat: -33.46, lng: -70.65, tz: -4 },
  '瓦尔帕莱索': { lat: -33.05, lng: -71.62, tz: -4 },
  '利马':     { lat: -12.05, lng: -77.04, tz: -5 },
  '波哥大':   { lat:   4.71, lng: -74.07, tz: -5 },
  '麦德林':   { lat:   6.25, lng: -75.56, tz: -5 },
  '卡利':     { lat:   3.44, lng: -76.52, tz: -5 },
  '卡拉卡斯': { lat:  10.48, lng: -66.87, tz: -4 },
  '基多':     { lat:  -0.23, lng: -78.52, tz: -5 },
  '拉巴斯':   { lat: -16.50, lng: -68.15, tz: -4 },
  '蒙得维的亚': { lat: -34.90, lng: -56.19, tz: -3 },
  '亚松森':   { lat: -25.29, lng: -57.65, tz: -4 },
  // ─── 非洲 ──────────────────────────────────────────────────────────
  '开罗':     { lat:  30.06, lng:  31.25, tz:  2 },
  '亚历山大': { lat:  31.21, lng:  29.92, tz:  2 },
  '卡萨布兰卡': { lat: 33.59, lng:  -7.62, tz:  1 },
  '拉巴特':   { lat:  34.01, lng:  -6.84, tz:  1 },
  '突尼斯城': { lat:  36.82, lng:  10.17, tz:  1 },
  '阿尔及尔': { lat:  36.74, lng:   3.06, tz:  1 },
  '的黎波里': { lat:  32.90, lng:  13.18, tz:  2 },
  '开普敦':   { lat: -33.93, lng:  18.42, tz:  2 },
  '约翰内斯堡': { lat: -26.20, lng:  28.04, tz:  2 },
  '德班':     { lat: -29.86, lng:  31.02, tz:  2 },
  '内罗毕':   { lat:  -1.29, lng:  36.82, tz:  3 },
  '内罗毕2':  { lat:  -1.29, lng:  36.82, tz:  3 },
  '亚的斯亚贝巴': { lat:  9.02, lng:  38.75, tz:  3 },
  '拉各斯':   { lat:   6.45, lng:   3.39, tz:  1 },
  '阿布贾':   { lat:   9.07, lng:   7.40, tz:  1 },
  '阿克拉':   { lat:   5.56, lng:  -0.21, tz:  0 },
  '达喀尔':   { lat:  14.72, lng: -17.47, tz:  0 },
  '阿比让':   { lat:   5.35, lng:  -4.00, tz:  0 },
  '金沙萨':   { lat:  -4.32, lng:  15.32, tz:  1 },
  '卢萨卡':   { lat: -15.42, lng:  28.28, tz:  2 },
  '哈拉雷':   { lat: -17.83, lng:  31.05, tz:  2 },
  '马普托':   { lat: -25.97, lng:  32.59, tz:  2 },
  '安塔那那利佛': { lat: -18.91, lng: 47.54, tz: 3 },
  // ─── 大洋洲 ────────────────────────────────────────────────────────
  '悉尼':     { lat: -33.87, lng: 151.21, tz: 10 },
  '墨尔本':   { lat: -37.81, lng: 144.96, tz: 10 },
  '布里斯班': { lat: -27.47, lng: 153.03, tz: 10 },
  '珀斯':     { lat: -31.95, lng: 115.86, tz:  8 },
  '阿德莱德': { lat: -34.93, lng: 138.60, tz:  9.5 },
  '堪培拉':   { lat: -35.28, lng: 149.13, tz: 10 },
  '达尔文':   { lat: -12.46, lng: 130.84, tz:  9.5 },
  '黄金海岸': { lat: -28.00, lng: 153.43, tz: 10 },
  '奥克兰':   { lat: -36.87, lng: 174.77, tz: 12 },
  '惠灵顿':   { lat: -41.29, lng: 174.78, tz: 12 },
  '基督城':   { lat: -43.53, lng: 172.63, tz: 12 },
  '苏瓦':     { lat:  -18.14, lng: 178.44, tz: 12 },
  '努美阿':   { lat: -22.27, lng: 166.46, tz: 11 },
  '帕皮提':   { lat: -17.53, lng:-149.57, tz:-10 },
  // ─── 特殊地区 ───────────────────────────────────────────────────────
  '夏威夷':   { lat:  21.31, lng:-157.86, tz:-10 },
  '檀香山':   { lat:  21.31, lng:-157.86, tz:-10 },
  '阿拉斯加': { lat:  61.19, lng:-149.90, tz: -9 },
  '安克雷奇': { lat:  61.22, lng:-149.90, tz: -9 },
};

// ─── 英文名 / 拼音名 / 别名 → 中文城市名 索引 ──────────────────────────────
const CITY_ALIAS: Record<string, string> = {
  // ─── 英文名 ────────────────────────────────────────────────────
  'beijing':       '北京市',
  'tianjin':       '天津市',
  'shanghai':      '上海市',
  'chongqing':     '重庆市',
  'shenzhen':      '深圳市',
  'guangzhou':     '广州市',
  'chengdu':       '成都市',
  'wuhan':         '武汉市',
  'xian':          '西安市',
  "xi'an":         '西安市',
  
  'hangzhou':      '杭州市',
  'suzhou':        '苏州市',
  'qingdao':       '青岛市',
  'dalian':        '大连市',
  'ningbo':        '宁波市',
  'wuxi':          '无锡市',
  'fuzhou':        '福州市',
  'xiamen':        '厦门市',
  'jinan':         '济南市',
  'zhengzhou':     '郑州市',
  'changsha':      '长沙市',
  'kunming':       '昆明市',
  'guiyang':       '贵阳市',
  'nanning':       '南宁市',
  'hefei':         '合肥市',
  'nanchang':      '南昌市',
  'harbin':        '哈尔滨市',
  'changchun':     '长春市',
  'shenyang':      '沈阳市',
  'hohhot':        '呼和浩特市',
  'taiyuan':       '太原市',
  'shijiazhuang':  '石家庄市',
  'lhasa':         '拉萨市',
  'urumqi':        '乌鲁木齐市',
  'yinchuan':      '银川市',
  'xining':        '西宁市',
  'lanzhou':       '兰州市',
  'nanjing':       '南京市',
  'taipei':        '台北市',
  'taichung':      '台中市',
  'kaohsiung':     '高雄市',
  'tainan':        '台南市',
  'hongkong':      '香港',
  'hong kong':     '香港',
  'macau':         '澳门',
  'macao':         '澳门',
  // 日本
  'tokyo':         '东京',
  'osaka':         '大阪',
  'kyoto':         '京都',
  'nagoya':        '名古屋',
  'yokohama':      '横滨',
  'kobe':          '神户',
  'fukuoka':       '福冈',
  'hiroshima':     '广岛',
  'sendai':        '仙台',
  'sapporo':       '札幌',
  'naha':          '那霸',
  // 韩国
  'seoul':         '首尔',
  'busan':         '釜山',
  'incheon':       '仁川',
  'daegu':         '大邱',
  'daejeon':       '大田',
  'gwangju':       '光州',
  // 东南亚
  'bangkok':       '曼谷',
  'singapore':     '新加坡',
  'kuala lumpur':  '吉隆坡',
  'kl':            '吉隆坡',
  'penang':        '槟城',
  'jakarta':       '雅加达',
  'surabaya':      '泗水',
  'bali':          '巴厘岛',
  'hanoi':         '河内',
  'ho chi minh':   '胡志明市',
  'saigon':        '胡志明市',
  'hcmc':          '胡志明市',
  'manila':        '马尼拉',
  'cebu':          '宿雾',
  'phnom penh':    '金边',
  'yangon':        '仰光',
  'rangoon':       '仰光',
  'dhaka':         '达卡',
  'kathmandu':     '加德满都',
  'colombo':       '科伦坡',
  'karachi':       '卡拉奇',
  'lahore':        '拉合尔',
  'islamabad':     '伊斯兰堡',
  // 南亚
  'mumbai':        '孟买',
  'bombay':        '孟买',
  'new delhi':     '新德里',
  'delhi':         '新德里',
  'bangalore':     '班加罗尔',
  'bengaluru':     '班加罗尔',
  'chennai':       '钦奈',
  'madras':        '钦奈',
  'hyderabad':     '海得拉巴',
  'kolkata':       '加尔各答',
  'calcutta':      '加尔各答',
  'ahmedabad':     '艾哈迈达巴德',
  'pune':          '浦那',
  // 中东
  'dubai':         '迪拜',
  'abu dhabi':     '阿布扎比',
  'doha':          '多哈',
  'riyadh':        '利雅得',
  'jeddah':        '吉达',
  'muscat':        '马斯喀特',
  'baghdad':       '巴格达',
  'tehran':        '德黑兰',
  'istanbul':      '伊斯坦布尔',
  'ankara':        '安卡拉',
  'tel aviv':      '特拉维夫',
  'jerusalem':     '耶路撒冷',
  'amman':         '安曼',
  'beirut':        '贝鲁特',
  'damascus':      '大马士革',
  // 欧洲
  'london':        '伦敦',
  'manchester':    '曼彻斯特',
  'birmingham':    '伯明翰',
  'glasgow':       '格拉斯哥',
  'liverpool':     '利物浦',
  'edinburgh':     '爱丁堡',
  'dublin':        '都柏林',
  'paris':         '巴黎',
  'lyon':          '里昂',
  'marseille':     '马赛',
  'berlin':        '柏林',
  'frankfurt':     '法兰克福',
  'hamburg':       '汉堡',
  'munich':        '慕尼黑',
  'cologne':       '科隆',
  'rome':          '罗马',
  'milan':         '米兰',
  'naples':        '那不勒斯',
  'florence':      '佛罗伦萨',
  'venice':        '威尼斯',
  'turin':         '都灵',
  'madrid':        '马德里',
  'barcelona':     '巴塞罗那',
  'seville':       '塞维利亚',
  'lisbon':        '里斯本',
  'porto':         '波尔图',
  'amsterdam':     '阿姆斯特丹',
  'rotterdam':     '鹿特丹',
  'brussels':      '布鲁塞尔',
  'vienna':        '维也纳',
  'zurich':        '苏黎世',
  'geneva':        '日内瓦',
  'bern':          '伯尔尼',
  'stockholm':     '斯德哥尔摩',
  'gothenburg':    '哥德堡',
  'copenhagen':    '哥本哈根',
  'oslo':          '奥斯陆',
  'helsinki':      '赫尔辛基',
  'reykjavik':     '雷克雅未克',
  'moscow':        '莫斯科',
  'st. petersburg': '圣彼得堡',
  'saint petersburg': '圣彼得堡',
  'novosibirsk':   '新西伯利亚',
  'ekaterinburg':  '叶卡捷琳堡',
  'warsaw':        '华沙',
  'krakow':        '克拉科夫',
  'prague':        '布拉格',
  'bratislava':    '布拉迪斯拉发',
  'budapest':      '布达佩斯',
  'bucharest':     '布加勒斯特',
  'belgrade':      '贝尔格莱德',
  'sofia':         '索菲亚',
  'athens':        '雅典',
  'kyiv':          '基辅',
  'kiev':          '基辅',
  'minsk':         '明斯克',
  'riga':          '里加',
  'tallinn':       '塔林',
  'vilnius':       '维尔纽斯',
  'zagreb':        '萨格勒布',
  'sarajevo':      '萨拉热窝',
  'tirana':        '蒂拉纳',
  'skopje':        '斯科普里',
  // 北美
  'new york':      '纽约',
  'nyc':           '纽约',
  'los angeles':   '洛杉矶',
  'la':            '洛杉矶',
  'san francisco': '旧金山',
  'sf':            '旧金山',
  'chicago':       '芝加哥',
  'houston':       '休斯顿',
  'phoenix':       '凤凰城',
  'miami':         '迈阿密',
  'boston':        '波士顿',
  'seattle':       '西雅图',
  'las vegas':     '拉斯维加斯',
  'denver':        '丹佛',
  'dallas':        '达拉斯',
  'san diego':     '圣地亚哥',
  'san jose':      '圣何塞',
  'austin':        '奥斯汀',
  'charlotte':     '夏洛特',
  'detroit':       '底特律',
  'washington':    '华盛顿',
  'washington dc': '华盛顿',
  'philadelphia':  '费城',
  'atlanta':       '亚特兰大',
  'portland':      '波特兰',
  'salt lake city': '盐湖城',
  'toronto':       '多伦多',
  'vancouver':     '温哥华',
  'montreal':      '蒙特利尔',
  'calgary':       '卡尔加里',
  'edmonton':      '埃德蒙顿',
  'ottawa':        '渥太华',
  'mexico city':   '墨西哥城',
  // 南美
  'sao paulo':     '圣保罗',
  'rio de janeiro': '里约热内卢',
  'brasilia':      '巴西利亚',
  'buenos aires':  '布宜诺斯艾利斯',
  'santiago':      '圣地亚哥智利',
  'lima':          '利马',
  'bogota':        '波哥大',
  'caracas':       '卡拉卡斯',
  'quito':         '基多',
  'montevideo':    '蒙得维的亚',
  // 非洲
  'cairo':         '开罗',
  'casablanca':    '卡萨布兰卡',
  'johannesburg':  '约翰内斯堡',
  'cape town':     '开普敦',
  'nairobi':       '内罗毕',
  'lagos':         '拉各斯',
  'accra':         '阿克拉',
  'addis ababa':   '亚的斯亚贝巴',
  'dar es salaam': '达累斯萨拉姆',
  // 大洋洲
  'sydney':        '悉尼',
  'melbourne':     '墨尔本',
  'brisbane':      '布里斯班',
  'perth':         '珀斯',
  'adelaide':      '阿德莱德',
  'canberra':      '堪培拉',
  'auckland':      '奥克兰',
  'wellington':    '惠灵顿',
  'christchurch':  '基督城',
  'honolulu':      '檀香山',
  'hawaii':        '夏威夷',
  'anchorage':     '安克雷奇',
  // 拼音
  'beijing shi':   '北京市',
  'shanghai shi':  '上海市',
  'guangzhou shi': '广州市',
  'shenzhen shi':  '深圳市',
  'chengdu shi':   '成都市',
};

// ─── 省份默认坐标 ─────────────────────────────────────────────────────
const PROVINCE_COORDS: Record<string, { lat: number; lng: number }> = {
  '北京市': { lat: 39.92, lng: 116.46 },
  '天津市': { lat: 39.13, lng: 117.20 },
  '上海市': { lat: 31.23, lng: 121.47 },
  '重庆市': { lat: 29.56, lng: 106.55 },
  '河北省': { lat: 38.04, lng: 114.48 },
  '山西省': { lat: 37.87, lng: 112.55 },
  '内蒙古自治区': { lat: 40.82, lng: 111.65 },
  '内蒙古': { lat: 40.82, lng: 111.65 },
  '辽宁省': { lat: 41.80, lng: 123.43 },
  '吉林省': { lat: 43.88, lng: 125.35 },
  '黑龙江省': { lat: 45.75, lng: 126.63 },
  '江苏省': { lat: 32.06, lng: 118.78 },
  '浙江省': { lat: 30.25, lng: 120.17 },
  '安徽省': { lat: 31.86, lng: 117.28 },
  '福建省': { lat: 26.08, lng: 119.30 },
  '江西省': { lat: 28.68, lng: 115.88 },
  '山东省': { lat: 36.67, lng: 116.99 },
  '河南省': { lat: 34.76, lng: 113.65 },
  '湖北省': { lat: 30.58, lng: 114.31 },
  '湖南省': { lat: 28.23, lng: 112.94 },
  '广东省': { lat: 23.13, lng: 113.26 },
  '广西壮族自治区': { lat: 22.82, lng: 108.37 },
  '广西': { lat: 22.82, lng: 108.37 },
  '海南省': { lat: 20.04, lng: 110.35 },
  '四川省': { lat: 30.67, lng: 104.07 },
  '贵州省': { lat: 26.58, lng: 106.72 },
  '云南省': { lat: 25.04, lng: 102.73 },
  '西藏自治区': { lat: 29.65, lng: 91.13 },
  '西藏': { lat: 29.65, lng: 91.13 },
  '陕西省': { lat: 34.27, lng: 108.95 },
  '甘肃省': { lat: 36.06, lng: 103.79 },
  '青海省': { lat: 36.62, lng: 101.78 },
  '宁夏回族自治区': { lat: 38.47, lng: 106.27 },
  '宁夏': { lat: 38.47, lng: 106.27 },
  '新疆维吾尔自治区': { lat: 43.82, lng: 87.62 },
  '新疆': { lat: 43.82, lng: 87.62 },
  '香港特别行政区': { lat: 22.32, lng: 114.17 },
  '澳门特别行政区': { lat: 22.20, lng: 113.55 },
  '台湾': { lat: 25.05, lng: 121.53 },
};

// ─── DST 规则 ─────────────────────────────────────────────────────────
interface DSTRule {
  stdOffset: number;
  dstOffset: number;
  dstStart: [number, number, number, number];
  dstEnd: [number, number, number, number];
  yearRange?: [number | null, number | null];
}

const DST_RULES: Record<string, DSTRule[]> = {
  'America/New_York': [
    { stdOffset: -5, dstOffset: -4, dstStart: [3, 2, 0, 2], dstEnd: [11, 1, 0, 2], yearRange: [2007, null] },
    { stdOffset: -5, dstOffset: -4, dstStart: [4, 1, 0, 2], dstEnd: [10, -1, 0, 2], yearRange: [1987, 2006] },
  ],
  'America/Chicago': [
    { stdOffset: -6, dstOffset: -5, dstStart: [3, 2, 0, 2], dstEnd: [11, 1, 0, 2], yearRange: [2007, null] },
    { stdOffset: -6, dstOffset: -5, dstStart: [4, 1, 0, 2], dstEnd: [10, -1, 0, 2], yearRange: [1987, 2006] },
  ],
  'America/Denver': [
    { stdOffset: -7, dstOffset: -6, dstStart: [3, 2, 0, 2], dstEnd: [11, 1, 0, 2], yearRange: [2007, null] },
    { stdOffset: -7, dstOffset: -6, dstStart: [4, 1, 0, 2], dstEnd: [10, -1, 0, 2], yearRange: [1987, 2006] },
  ],
  'America/Los_Angeles': [
    { stdOffset: -8, dstOffset: -7, dstStart: [3, 2, 0, 2], dstEnd: [11, 1, 0, 2], yearRange: [2007, null] },
    { stdOffset: -8, dstOffset: -7, dstStart: [4, 1, 0, 2], dstEnd: [10, -1, 0, 2], yearRange: [1987, 2006] },
  ],
  'America/Toronto': [
    { stdOffset: -5, dstOffset: -4, dstStart: [3, 2, 0, 2], dstEnd: [11, 1, 0, 2], yearRange: [2007, null] },
    { stdOffset: -5, dstOffset: -4, dstStart: [4, 1, 0, 2], dstEnd: [10, -1, 0, 2], yearRange: [1987, 2006] },
  ],
  'America/Vancouver': [
    { stdOffset: -8, dstOffset: -7, dstStart: [3, 2, 0, 2], dstEnd: [11, 1, 0, 2], yearRange: [2007, null] },
    { stdOffset: -8, dstOffset: -7, dstStart: [4, 1, 0, 2], dstEnd: [10, -1, 0, 2], yearRange: [1987, 2006] },
  ],
  'America/Denver_AZ': [
    // Arizona 不实行 DST，固定 -7
    { stdOffset: -7, dstOffset: -7, dstStart: [1, 1, 0, 0], dstEnd: [12, -1, 6, 23], yearRange: [null, null] },
  ],
  'America/Winnipeg': [
    { stdOffset: -6, dstOffset: -5, dstStart: [3, 2, 0, 2], dstEnd: [11, 1, 0, 2], yearRange: [2007, null] },
    { stdOffset: -6, dstOffset: -5, dstStart: [4, 1, 0, 2], dstEnd: [10, -1, 0, 2], yearRange: [1987, 2006] },
  ],
  'America/Halifax': [
    { stdOffset: -4, dstOffset: -3, dstStart: [3, 2, 0, 2], dstEnd: [11, 1, 0, 2], yearRange: [2007, null] },
    { stdOffset: -4, dstOffset: -3, dstStart: [4, 1, 0, 2], dstEnd: [10, -1, 0, 2], yearRange: [1987, 2006] },
  ],
  'America/Mexico_City': [
    { stdOffset: -6, dstOffset: -5, dstStart: [4, 1, 0, 2], dstEnd: [10, -1, 0, 2], yearRange: [1996, null] },
  ],
  'Europe/London': [
    { stdOffset: 0, dstOffset: 1, dstStart: [3, -1, 0, 1], dstEnd: [10, -1, 0, 1], yearRange: [1996, null] },
  ],
  'Europe/Paris': [{ stdOffset: 1, dstOffset: 2, dstStart: [3, -1, 0, 2], dstEnd: [10, -1, 0, 3], yearRange: [1996, null] }],
  'Europe/Berlin': [{ stdOffset: 1, dstOffset: 2, dstStart: [3, -1, 0, 2], dstEnd: [10, -1, 0, 3], yearRange: [1996, null] }],
  'Europe/Amsterdam': [{ stdOffset: 1, dstOffset: 2, dstStart: [3, -1, 0, 2], dstEnd: [10, -1, 0, 3], yearRange: [1996, null] }],
  'Europe/Rome': [{ stdOffset: 1, dstOffset: 2, dstStart: [3, -1, 0, 2], dstEnd: [10, -1, 0, 3], yearRange: [1996, null] }],
  'Europe/Madrid': [{ stdOffset: 1, dstOffset: 2, dstStart: [3, -1, 0, 2], dstEnd: [10, -1, 0, 3], yearRange: [1996, null] }],
  'Europe/Vienna': [{ stdOffset: 1, dstOffset: 2, dstStart: [3, -1, 0, 2], dstEnd: [10, -1, 0, 3], yearRange: [1996, null] }],
  'Europe/Zurich': [{ stdOffset: 1, dstOffset: 2, dstStart: [3, -1, 0, 2], dstEnd: [10, -1, 0, 3], yearRange: [1996, null] }],
  'Europe/Stockholm': [{ stdOffset: 1, dstOffset: 2, dstStart: [3, -1, 0, 2], dstEnd: [10, -1, 0, 3], yearRange: [1996, null] }],
  'Europe/Copenhagen': [{ stdOffset: 1, dstOffset: 2, dstStart: [3, -1, 0, 2], dstEnd: [10, -1, 0, 3], yearRange: [1996, null] }],
  'Europe/Oslo': [{ stdOffset: 1, dstOffset: 2, dstStart: [3, -1, 0, 2], dstEnd: [10, -1, 0, 3], yearRange: [1996, null] }],
  'Europe/Helsinki': [{ stdOffset: 2, dstOffset: 3, dstStart: [3, -1, 0, 3], dstEnd: [10, -1, 0, 4], yearRange: [1996, null] }],
  'Europe/Athens': [{ stdOffset: 2, dstOffset: 3, dstStart: [3, -1, 0, 3], dstEnd: [10, -1, 0, 4], yearRange: [1996, null] }],
  'Europe/Bucharest': [{ stdOffset: 2, dstOffset: 3, dstStart: [3, -1, 0, 3], dstEnd: [10, -1, 0, 4], yearRange: [1996, null] }],
  'Europe/Sofia': [{ stdOffset: 2, dstOffset: 3, dstStart: [3, -1, 0, 3], dstEnd: [10, -1, 0, 4], yearRange: [1996, null] }],
  'Europe/Kiev': [{ stdOffset: 2, dstOffset: 3, dstStart: [3, -1, 0, 3], dstEnd: [10, -1, 0, 4], yearRange: [1996, null] }],
  'Europe/Warsaw': [{ stdOffset: 1, dstOffset: 2, dstStart: [3, -1, 0, 2], dstEnd: [10, -1, 0, 3], yearRange: [1996, null] }],
  'Europe/Prague': [{ stdOffset: 1, dstOffset: 2, dstStart: [3, -1, 0, 2], dstEnd: [10, -1, 0, 3], yearRange: [1996, null] }],
  'Europe/Budapest': [{ stdOffset: 1, dstOffset: 2, dstStart: [3, -1, 0, 2], dstEnd: [10, -1, 0, 3], yearRange: [1996, null] }],
  'Europe/Belgrade': [{ stdOffset: 1, dstOffset: 2, dstStart: [3, -1, 0, 2], dstEnd: [10, -1, 0, 3], yearRange: [1996, null] }],
  'Europe/Lisbon': [{ stdOffset: 0, dstOffset: 1, dstStart: [3, -1, 0, 1], dstEnd: [10, -1, 0, 1], yearRange: [1996, null] }],
  'Africa/Johannesburg': [],
  'Australia/Sydney': [
    { stdOffset: 10, dstOffset: 11, dstStart: [10, 1, 0, 2], dstEnd: [4, 1, 0, 3], yearRange: [2008, null] },
    { stdOffset: 10, dstOffset: 11, dstStart: [10, -1, 0, 2], dstEnd: [3, -1, 0, 3], yearRange: [1971, 2007] },
  ],
  'Australia/Melbourne': [
    { stdOffset: 10, dstOffset: 11, dstStart: [10, 1, 0, 2], dstEnd: [4, 1, 0, 3], yearRange: [2008, null] },
    { stdOffset: 10, dstOffset: 11, dstStart: [10, -1, 0, 2], dstEnd: [3, -1, 0, 3], yearRange: [1971, 2007] },
  ],
  'Australia/Brisbane': [],  // 昆士兰不实行 DST
  'Australia/Adelaide': [
    { stdOffset: 9.5, dstOffset: 10.5, dstStart: [10, 1, 0, 2], dstEnd: [4, 1, 0, 3], yearRange: [2008, null] },
  ],
  'Pacific/Auckland': [
    { stdOffset: 12, dstOffset: 13, dstStart: [9, -1, 0, 2], dstEnd: [4, 1, 0, 3], yearRange: [2008, null] },
    { stdOffset: 12, dstOffset: 13, dstStart: [10, 1, 0, 2], dstEnd: [3, 1, 0, 3], yearRange: [1990, 2007] },
  ],
  'Asia/Israel': [
    { stdOffset: 2, dstOffset: 3, dstStart: [3, -1, 5, 2], dstEnd: [10, -1, 0, 2], yearRange: [2013, null] },
  ],
};

// 城市 → IANA 时区标识
const CITY_IANA: Record<string, string> = {
  '纽约': 'America/New_York', '迈阿密': 'America/New_York', '波士顿': 'America/New_York',
  '华盛顿': 'America/New_York', '费城': 'America/New_York', '亚特兰大': 'America/New_York',
  '底特律': 'America/New_York', '杰克逊维尔': 'America/New_York', '哥伦布': 'America/New_York',
  '夏洛特': 'America/New_York', '印第安纳波利斯': 'America/New_York',
  '多伦多': 'America/Toronto', '蒙特利尔': 'America/Toronto', '渥太华': 'America/Toronto',
  '哈利法克斯': 'America/Halifax',
  '芝加哥': 'America/Chicago', '休斯顿': 'America/Chicago', '达拉斯': 'America/Chicago',
  '奥斯汀': 'America/Chicago', '圣安东尼奥': 'America/Chicago', '明尼阿波利斯': 'America/Chicago',
  '圣路易斯': 'America/Chicago', '奥尔良': 'America/Chicago', '温尼伯': 'America/Winnipeg',
  '墨西哥城': 'America/Mexico_City', '瓜达拉哈拉': 'America/Mexico_City', '蒙特雷': 'America/Mexico_City',
  '丹佛': 'America/Denver', '盐湖城': 'America/Denver', '卡尔加里': 'America/Denver',
  '埃德蒙顿': 'America/Denver', '阿尔伯克基': 'America/Denver',
  '洛杉矶': 'America/Los_Angeles', '旧金山': 'America/Los_Angeles', '西雅图': 'America/Los_Angeles',
  '拉斯维加斯': 'America/Los_Angeles', '圣地亚哥': 'America/Los_Angeles',
  '圣何塞': 'America/Los_Angeles', '波特兰': 'America/Los_Angeles', '萨克拉门托': 'America/Los_Angeles',
  '温哥华': 'America/Vancouver',
  '伦敦': 'Europe/London', '曼彻斯特': 'Europe/London', '伯明翰': 'Europe/London',
  '格拉斯哥': 'Europe/London', '利物浦': 'Europe/London', '爱丁堡': 'Europe/London',
  '都柏林': 'Europe/London',
  '里斯本': 'Europe/Lisbon', '波尔图': 'Europe/Lisbon',
  '巴黎': 'Europe/Paris', '里昂': 'Europe/Paris', '马赛': 'Europe/Paris',
  '波尔多': 'Europe/Paris', '里尔': 'Europe/Paris', '图卢兹': 'Europe/Paris', '尼斯': 'Europe/Paris',
  '柏林': 'Europe/Berlin', '法兰克福': 'Europe/Berlin', '汉堡': 'Europe/Berlin',
  '慕尼黑': 'Europe/Berlin', '科隆': 'Europe/Berlin', '斯图加特': 'Europe/Berlin',
  '莱比锡': 'Europe/Berlin', '德累斯顿': 'Europe/Berlin', '杜塞尔多夫': 'Europe/Berlin',
  '阿姆斯特丹': 'Europe/Amsterdam', '鹿特丹': 'Europe/Amsterdam',
  '布鲁塞尔': 'Europe/Amsterdam', '安特卫普': 'Europe/Amsterdam',
  '罗马': 'Europe/Rome', '米兰': 'Europe/Rome', '那不勒斯': 'Europe/Rome',
  '佛罗伦萨': 'Europe/Rome', '威尼斯': 'Europe/Rome', '都灵': 'Europe/Rome',
  '热那亚': 'Europe/Rome', '博洛尼亚': 'Europe/Rome', '巴勒莫': 'Europe/Rome',
  '马德里': 'Europe/Madrid', '巴塞罗那': 'Europe/Madrid', '瓦伦西亚': 'Europe/Madrid',
  '塞维利亚': 'Europe/Madrid', '马拉加': 'Europe/Madrid',
  '维也纳': 'Europe/Vienna', '格拉茨': 'Europe/Vienna',
  '苏黎世': 'Europe/Zurich', '日内瓦': 'Europe/Zurich', '巴塞尔': 'Europe/Zurich', '伯尔尼': 'Europe/Zurich',
  '斯德哥尔摩': 'Europe/Stockholm', '哥德堡': 'Europe/Stockholm', '马尔默': 'Europe/Stockholm',
  '哥本哈根': 'Europe/Copenhagen',
  '奥斯陆': 'Europe/Oslo', '卑尔根': 'Europe/Oslo',
  '赫尔辛基': 'Europe/Helsinki', '坦佩雷': 'Europe/Helsinki',
  '雅典': 'Europe/Athens', '萨洛尼卡': 'Europe/Athens',
  '布加勒斯特': 'Europe/Bucharest',
  '索菲亚': 'Europe/Sofia',
  '基辅': 'Europe/Kiev', '哈尔科夫': 'Europe/Kiev', '敖德萨': 'Europe/Kiev',
  '华沙': 'Europe/Warsaw', '克拉科夫': 'Europe/Warsaw',
  '布拉格': 'Europe/Prague',
  '布达佩斯': 'Europe/Budapest', '布拉迪斯拉发': 'Europe/Budapest',
  '贝尔格莱德': 'Europe/Belgrade', '萨格勒布': 'Europe/Belgrade',
  '萨拉热窝': 'Europe/Belgrade', '卢布尔雅那': 'Europe/Belgrade',
  '约翰内斯堡': 'Africa/Johannesburg', '开普敦': 'Africa/Johannesburg', '德班': 'Africa/Johannesburg',
  '悉尼': 'Australia/Sydney', '堪培拉': 'Australia/Sydney',
  '墨尔本': 'Australia/Melbourne',
  '布里斯班': 'Australia/Brisbane', '黄金海岸': 'Australia/Brisbane',
  '阿德莱德': 'Australia/Adelaide',
  '奥克兰': 'Pacific/Auckland', '惠灵顿': 'Pacific/Auckland', '基督城': 'Pacific/Auckland',
  '特拉维夫': 'Asia/Israel', '耶路撒冷': 'Asia/Israel',
};

function getNthWeekdayOfMonth(year: number, month: number, week: number, dow: number): Date {
  if (week === -1) {
    const lastDay = new Date(Date.UTC(year, month, 0));
    let d = lastDay.getUTCDay();
    let diff = d - dow;
    if (diff < 0) diff += 7;
    return new Date(Date.UTC(year, month - 1, lastDay.getUTCDate() - diff));
  }
  const firstDay = new Date(Date.UTC(year, month - 1, 1));
  let d = firstDay.getUTCDay();
  let diff = dow - d;
  if (diff < 0) diff += 7;
  return new Date(Date.UTC(year, month - 1, 1 + diff + (week - 1) * 7));
}

function getHistoricalOffset(
  ianaKey: string,
  year: number, month: number, day: number, hour: number,
): number | null {
  const rules = DST_RULES[ianaKey];
  if (!rules || rules.length === 0) return null;

  const applicableRules = rules.filter(r => {
    if (!r.yearRange) return true;
    const [start, end] = r.yearRange;
    if (start !== null && year < start) return false;
    if (end !== null && year > end) return false;
    return true;
  });

  if (applicableRules.length === 0) return null;
  const rule = applicableRules[0];

  // 完全无 DST 城市（dstStart == dstEnd == 标准时）
  if (rule.stdOffset === rule.dstOffset) return rule.stdOffset;

  const [dsm, dsw, dsd, dsh] = rule.dstStart;
  const [dem, dew, ded, deh] = rule.dstEnd;

  const dstStartDate = getNthWeekdayOfMonth(year, dsm, dsw, dsd);
  const dstEndDate   = getNthWeekdayOfMonth(year, dem, dew, ded);
  dstStartDate.setUTCHours(dsh);
  dstEndDate.setUTCHours(deh);

  const testDate = new Date(Date.UTC(year, month - 1, day, hour));

  let inDST: boolean;
  if (dstStartDate > dstEndDate) {
    inDST = testDate >= dstStartDate || testDate < dstEndDate;
  } else {
    inDST = testDate >= dstStartDate && testDate < dstEndDate;
  }

  return inDST ? rule.dstOffset : rule.stdOffset;
}

/**
 * 从出生地字符串解析经纬度 + 时区
 * 优先级：显式 GMT > DST历史推算 > 城市库静态 > 省份回退 > 仅GMT
 */
export function parseCoordsFromBirthPlace(
  birthPlace: string | undefined | null,
  birthYear?: number,
  birthMonth?: number,
  birthDay?: number,
  birthHour?: number,
): GeoCoords | null {
  if (!birthPlace) return null;
  const raw = birthPlace.trim();
  if (raw === '--' || raw === '' || raw === '未知') return null;

  // 提取显式 GMT offset
  let explicitTz: number | null = null;
  const gmtMatch = raw.match(/\(GMT([+-])(\d+(?:\.\d+)?)\)/i);
  if (gmtMatch) {
    const sign = gmtMatch[1] === '-' ? -1 : 1;
    explicitTz = sign * parseFloat(gmtMatch[2]);
  }

  const locationText = raw.replace(/\s*\(GMT[^)]*\)/gi, ' ').trim();
  const cleanTokens = locationText.split(/[\s#／/\-、，,]+/).filter(Boolean);

  const resolveTimezone = (cityName: string, baseCoords: GeoCoords): GeoCoords => {
    if (birthYear !== undefined && birthMonth !== undefined && birthDay !== undefined) {
      const ianaKey = CITY_IANA[cityName];
      if (ianaKey) {
        const hist = getHistoricalOffset(ianaKey, birthYear, birthMonth, birthDay, birthHour ?? 12);
        if (hist !== null) return { ...baseCoords, tz: hist };
      }
    }
    if (explicitTz !== null) return { ...baseCoords, tz: explicitTz };
    return baseCoords;
  };

  const areaMatch = resolveAreaDataCoords(cleanTokens);
  if (areaMatch) return resolveTimezone(areaMatch.resolvedName, areaMatch.coords);

  // 1. 精确匹配城市库
  for (let i = cleanTokens.length - 1; i >= 0; i--) {
    const city = cleanTokens[i];
    if (CITY_COORDS[city]) return resolveTimezone(city, CITY_COORDS[city]);
  }

  // 2. 英文名 / 拼音名 / 别名索引（不区分大小写）
  const rawLower = locationText.toLowerCase();
  // 先尝试整体匹配
  if (CITY_ALIAS[rawLower]) {
    const chName = CITY_ALIAS[rawLower];
    if (CITY_COORDS[chName]) return resolveTimezone(chName, CITY_COORDS[chName]);
  }
  // 再按 token 匹配
  for (let i = cleanTokens.length - 1; i >= 0; i--) {
    const tk = cleanTokens[i].toLowerCase();
    if (CITY_ALIAS[tk]) {
      const chName = CITY_ALIAS[tk];
      if (CITY_COORDS[chName]) return resolveTimezone(chName, CITY_COORDS[chName]);
    }
  }
  // 多词组合匹配（如 "New York"）
  for (const [alias, chName] of Object.entries(CITY_ALIAS)) {
    if (rawLower.includes(alias) && alias.length >= 4) {
      if (CITY_COORDS[chName]) return resolveTimezone(chName, CITY_COORDS[chName]);
    }
  }

  // 3. 去掉"市/省/区"后缀的模糊匹配
  for (let i = cleanTokens.length - 1; i >= 0; i--) {
    const city = cleanTokens[i].replace(/[市省区县]/g, '');
    for (const [key, coords] of Object.entries(CITY_COORDS)) {
      const keyClean = key.replace(/[市省区县]/g, '');
      if (keyClean === city || keyClean.startsWith(city) || city.startsWith(keyClean)) {
        return resolveTimezone(key, coords);
      }
    }
  }

  // 4. 省份回退
  const provinceKeys = Object.keys(PROVINCE_COORDS);
  for (const token of cleanTokens) {
    for (const provKey of provinceKeys) {
      const provClean = provKey.replace(/[省自治区特别行政区]/g, '');
      if (token === provKey || provKey.startsWith(token) || token === provClean) {
        const pCoords = PROVINCE_COORDS[provKey];
        return { lat: pCoords.lat, lng: pCoords.lng, tz: explicitTz ?? 8 };
      }
    }
  }

  // 5. 仅有显式 GMT
  if (explicitTz !== null) {
    return { lat: 20, lng: explicitTz * 15, tz: explicitTz };
  }

  return null;
}

/** 默认坐标：中国地理中心，UTC+8 */
export const DEFAULT_GEO: GeoCoords = { lat: 35.0, lng: 105.0, tz: 8 };

// ─── 扩充别名：简写/带市后缀/拼音变体 ─────────────────────────────────
const CITY_ALIAS_EXTRA: Record<string, string> = {
  // 国内"无市后缀"常见简写
  '上海':   '上海市', '北京': '北京市', '天津': '天津市', '重庆': '重庆市',
  '深圳':   '深圳市', '广州': '广州市', '成都': '成都市', '武汉': '武汉市',
  '西安':   '西安市', '南京': '南京市', '杭州': '杭州市', '苏州': '苏州市',
  '青岛':   '青岛市', '大连': '大连市', '宁波': '宁波市', '无锡': '无锡市',
  '福州':   '福州市', '厦门': '厦门市', '济南': '济南市', '郑州': '郑州市',
  '长沙':   '长沙市', '昆明': '昆明市', '贵阳': '贵阳市', '南宁': '南宁市',
  '合肥':   '合肥市', '南昌': '南昌市', '长春': '长春市', '沈阳': '沈阳市',
  '呼和浩特': '呼和浩特市', '太原': '太原市', '石家庄': '石家庄市',
  '哈尔滨': '哈尔滨市', '拉萨': '拉萨市', '乌鲁木齐': '乌鲁木齐市',
  '银川':   '银川市', '西宁': '西宁市', '兰州': '兰州市',
  '海口':   '海口市', '三亚': '海口市',
  '台北':   '台北市', '台中': '台中市', '高雄': '高雄市', '台南': '台南市',
  '新竹':   '新竹市', '基隆': '基隆市',
  // 国内带"市"但库里无完全匹配的变体
  '北京市': '北京市', '上海市': '上海市',
  // 海外城市中文常见多写法
  '纽约市':   '纽约', '洛杉矶市': '洛杉矶', '旧金山市': '旧金山',
  '芝加哥市': '芝加哥', '休斯顿市': '休斯顿', '迈阿密市': '迈阿密',
  '波士顿市': '波士顿', '西雅图市': '西雅图', '华盛顿市': '华盛顿',
  '多伦多市': '多伦多', '温哥华市': '温哥华',
  '伦敦市':   '伦敦', '巴黎市': '巴黎', '柏林市': '柏林',
  '东京都':   '东京', '大阪市': '大阪', '京都市': '京都', '名古屋市': '名古屋',
  '横滨市':   '横滨', '神户市': '神户', '福冈市': '福冈', '广岛市': '广岛',
  '仙台市':   '仙台', '札幌市': '札幌',
  '首尔市':   '首尔', '釜山市': '釜山', '仁川市': '仁川',
  '悉尼市':   '悉尼', '墨尔本市': '墨尔本', '布里斯班市': '布里斯班',
  '新加坡市': '新加坡', '吉隆坡市': '吉隆坡',
  // 拼音 / 拼音变体
  'nanjing':    '南京市',
  'wuxi':       '无锡市',
  'nanchang':   '南昌市',
  'guiyang':    '贵阳市',
  'haikou':     '海口市',
  'sanya':      '海口市',
  'hefei':      '合肥市',
  'zhengzhou':  '郑州市',
  // 英文/常见输入变体
  'new york city': '纽约',
  'nyc':           '纽约',
  'la city':       '洛杉矶',
  'san francisco bay': '旧金山',
  'sf bay area':   '旧金山',
  'washington d.c.': '华盛顿',
  'washington, d.c.': '华盛顿',
  'dc':            '华盛顿',
  'toronto, canada': '多伦多',
  'tokyo, japan':  '东京',
  'osaka, japan':  '大阪',
  'seoul, korea':  '首尔',
  'beijing, china': '北京市',
  'shanghai, china': '上海市',
  // 拼音变体补充
  "xi'an":    '西安市',
  'xi an':    '西安市',
  'hong kong sar': '香港',
  'hk':       '香港',
  'macao sar': '澳门',
  // 中东/南亚常用变体
  'uae':      '迪拜',
  'ksa':      '利雅得',
};

// ─── 统一解析层 — 公开接口 ──────────────────────────────────────────────

/**
 * 出生地解析结果（标准化，供所有模块消费）
 *
 * 设计原则：
 * - 任何情况下都返回合法的 lat/lng/tz，不返回 null，调用方无需再做 ?? DEFAULT_GEO
 * - resolvedName 告知用户实际匹配到了哪个城市
 * - source 说明数据来源（供调试和展示用）
 * - isDefault 标记是否使用了默认值（未能匹配）
 */
export interface ResolvedLocation {
  lat: number;
  lng: number;
  tz: number;
  /** 实际匹配到的城市中文名（未能匹配时为 null） */
  resolvedName: string | null;
  /**
   * 数据来源说明
   * 'explicit_gmt' — 输入中含 (GMT±N)
   * 'city_exact'   — 城市库精确匹配
   * 'city_alias'   — 英文名/拼音/别名匹配
   * 'city_fuzzy'   — 去后缀模糊匹配
   * 'province'     — 省份回退
   * 'dst_adjusted' — 命中上述任意城市后，进一步 DST 历史修正
   * 'default'      — 未匹配，使用默认中国中心坐标
   */
  source: 'explicit_gmt' | 'city_exact' | 'city_alias' | 'city_fuzzy' | 'province' | 'dst_adjusted' | 'default';
  /** 是否使用了默认值（未能匹配任何城市） */
  isDefault: boolean;
}

/**
 * 统一出生地解析入口（全站唯一，替代散落在各页面的手工拼接）
 *
 * @param birthPlace 出生地字符串（可含 "(GMT+8)" 等偏移提示）
 * @param birthYear  出生年（用于 DST 历史时区修正）
 * @param birthMonth 出生月
 * @param birthDay   出生日
 * @param birthHour  出生时（用于 DST 边界判断）
 */
export function resolveBirthLocation(
  birthPlace: string | undefined | null,
  birthYear?: number,
  birthMonth?: number,
  birthDay?: number,
  birthHour?: number,
): ResolvedLocation {
  const DEFAULT_RESULT: ResolvedLocation = {
    lat: DEFAULT_GEO.lat, lng: DEFAULT_GEO.lng, tz: DEFAULT_GEO.tz,
    resolvedName: null, source: 'default', isDefault: true,
  };

  if (!birthPlace) return DEFAULT_RESULT;
  const raw = birthPlace.trim();
  if (raw === '' || raw === '--' || raw === '未知') return DEFAULT_RESULT;

  // 提取显式 GMT offset
  let explicitTz: number | null = null;
  const gmtMatch = raw.match(/\(GMT([+-])(\d+(?:\.\d+)?)\)/i);
  if (gmtMatch) {
    explicitTz = (gmtMatch[1] === '-' ? -1 : 1) * parseFloat(gmtMatch[2]);
  }

  const locationText = raw.replace(/\s*\(GMT[^)]*\)/gi, ' ').trim();
  const rawLower = locationText.toLowerCase();
  const cleanTokens = locationText.split(/[\s#／/\-、，,]+/).filter(Boolean);

  /**
   * 将 GeoCoords + cityName 转成 ResolvedLocation，
   * 同时尝试 DST 历史修正
   */
  const toResolved = (
    coords: GeoCoords,
    cityName: string,
    baseSource: ResolvedLocation['source'],
  ): ResolvedLocation => {
    if (birthYear !== undefined && birthMonth !== undefined && birthDay !== undefined) {
      const ianaKey = CITY_IANA[cityName];
      if (ianaKey) {
        const hist = getHistoricalOffset(ianaKey, birthYear, birthMonth, birthDay, birthHour ?? 12);
        if (hist !== null && hist !== coords.tz) {
          return { lat: coords.lat, lng: coords.lng, tz: hist, resolvedName: cityName, source: 'dst_adjusted', isDefault: false };
        }
      }
    }
    if (explicitTz !== null) {
      return { lat: coords.lat, lng: coords.lng, tz: explicitTz, resolvedName: cityName, source: 'explicit_gmt', isDefault: false };
    }
    return { lat: coords.lat, lng: coords.lng, tz: coords.tz, resolvedName: cityName, source: baseSource, isDefault: false };
  };

  const areaMatch = resolveAreaDataCoords(cleanTokens);
  if (areaMatch) {
    return toResolved(areaMatch.coords, areaMatch.resolvedName, areaMatch.source);
  }

  // ── 步骤 1：精确匹配城市库（从右往左，最具体的 token 优先）
  for (let i = cleanTokens.length - 1; i >= 0; i--) {
    const city = cleanTokens[i];
    if (CITY_COORDS[city]) return toResolved(CITY_COORDS[city], city, 'city_exact');
  }

  // ── 步骤 2：扩充别名表（先查额外别名，再查原有别名）
  const checkAlias = (key: string): ResolvedLocation | null => {
    const chName = CITY_ALIAS_EXTRA[key] ?? CITY_ALIAS[key];
    if (!chName) return null;
    const coords = CITY_COORDS[chName];
    if (!coords) return null;
    return toResolved(coords, chName, 'city_alias');
  };

  // 整体输入匹配
  const wholeResult = checkAlias(rawLower) ?? checkAlias(rawLower.replace(/市|省|区|县$/g, ''));
  if (wholeResult) return wholeResult;

  // 逐 token 匹配
  for (let i = cleanTokens.length - 1; i >= 0; i--) {
    const tk = cleanTokens[i].toLowerCase();
    const r = checkAlias(tk) ?? checkAlias(tk.replace(/市|省|区|县$/g, ''));
    if (r) return r;
  }

  // 多词组合匹配（如 "New York"、"San Francisco"）
  for (const [alias, chName] of Object.entries({ ...CITY_ALIAS_EXTRA, ...CITY_ALIAS })) {
    if (alias.length >= 3 && rawLower.includes(alias)) {
      const coords = CITY_COORDS[chName];
      if (coords) return toResolved(coords, chName, 'city_alias');
    }
  }

  // ── 步骤 3：去后缀模糊匹配
  for (let i = cleanTokens.length - 1; i >= 0; i--) {
    const city = cleanTokens[i].replace(/[市省区县]/g, '');
    if (!city || city.length < 2) continue;
    for (const [key, coords] of Object.entries(CITY_COORDS)) {
      const keyClean = key.replace(/[市省区县]/g, '');
      if (keyClean === city || keyClean.startsWith(city) || city.startsWith(keyClean)) {
        return toResolved(coords, key, 'city_fuzzy');
      }
    }
  }

  // ── 步骤 4：省份回退
  const provinceKeys = Object.keys(PROVINCE_COORDS);
  for (const token of cleanTokens) {
    for (const provKey of provinceKeys) {
      const provClean = provKey.replace(/[省自治区特别行政区]/g, '');
      if (token === provKey || provKey.startsWith(token) || token === provClean) {
        const pCoords = PROVINCE_COORDS[provKey];
        return { lat: pCoords.lat, lng: pCoords.lng, tz: explicitTz ?? 8, resolvedName: provKey, source: 'province', isDefault: false };
      }
    }
  }

  // ── 步骤 5：仅有显式 GMT
  if (explicitTz !== null) {
    return { lat: 20, lng: explicitTz * 15, tz: explicitTz, resolvedName: null, source: 'explicit_gmt', isDefault: false };
  }

  return DEFAULT_RESULT;
}

/**
 * 一体化接口：出生地字符串 + 出生时间字符串 → 完整占星档案（AstroProfile）
 *
 * 这是全站推荐的最高层调用入口，内部自动完成：
 *   parseBirthDateFull(birthStr) → year/month/day/hour
 *   resolveBirthLocation(birthPlace, ...) → lat/lng/tz
 *   buildAstroProfile(birthStr, lat, lng, tz) → AstroProfile
 *
 * 调用方无需再 import coreCalc / geoUtils，无需手工拼接参数。
 *
 * @param birthStr   出生时间字符串，如 "1995年06月15日 08:30"
 * @param birthPlace 出生地字符串，如 "上海" / "Tokyo" / "New York (GMT-5)"
 */
export function buildProfileFromBirth(
  birthStr: string | undefined,
  birthPlace: string | undefined | null,
): { profile: ReturnType<typeof _buildAstroProfile>; location: ResolvedLocation } {
  const parts = _parseBirthDateFull(birthStr);
  const loc   = resolveBirthLocation(birthPlace, parts.year, parts.month, parts.day, parts.hour);
  const profile = _buildAstroProfile(birthStr, loc.lat, loc.lng, loc.tz);
  return { profile, location: loc };
}

// 内部引用（避免循环依赖，由 index.ts 重新导出时使用 coreCalc 直接导入）
// 这里用 late binding，在模块加载完成后填充
let _buildAstroProfile: (solar: string | undefined, lat?: number, lng?: number, tz?: number) => import('./types').AstroProfile;
let _parseBirthDateFull: (solar: string | undefined) => { year: number; month: number; day: number; hour: number; minute: number };

export function _injectCoreCalc(
  buildFn: typeof _buildAstroProfile,
  parseFn: typeof _parseBirthDateFull,
): void {
  _buildAstroProfile = buildFn;
  _parseBirthDateFull = parseFn;
}
